import type { ReactNode } from "react";
import { Badge, Button, cn } from "@dearbill/shared";
import { AlertCircleIcon, ArrowRightIcon } from "../icons";

/** One pending step in the Action Needed panel. */
export interface PendingAction {
  key: string;
  title: string;
  description: string;
  /** The button label. */
  label: string;
  onAction: () => void;
}

/**
 * The signature calls to action, shown only while at least one step is
 * pending. The first step's button is the screen's single primary action; any
 * further step uses the secondary style, so the screen keeps one primary.
 */
export function ActionNeededPanel({ actions }: { actions: PendingAction[] }): ReactNode {
  return (
    <section className="border-warning-border bg-warning-bg flex flex-col gap-4 rounded-2xl border p-6">
      <div className="flex items-center justify-between gap-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="bg-lemon text-ochre flex size-8 shrink-0 items-center justify-center rounded-lg">
            <AlertCircleIcon size={18} />
          </span>
          <div className="flex min-w-0 flex-col gap-0.5">
            <h2 className="text-body-lg text-midnight font-bold">Action Needed</h2>
            <p className="text-body-sm text-slate-teal">
              {actions.length > 1
                ? "Complete these steps to keep your savings on track."
                : "Complete this step to keep your savings on track."}
            </p>
          </div>
        </div>
        <Badge tone="pending" className="shrink-0">
          Pending
        </Badge>
      </div>
      {actions.map((action, i) => (
        <div
          key={action.key}
          className={cn(
            "flex flex-col items-start gap-4 lg:flex-row lg:items-center",
            i > 0 && "border-warning-border border-t pt-4",
          )}
        >
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <p className="text-body text-midnight font-semibold">{action.title}</p>
            <p className="text-body-sm text-slate-teal">{action.description}</p>
          </div>
          <Button
            size="md"
            variant={i === 0 ? "primary" : "secondary"}
            onClick={action.onAction}
            className="shrink-0 gap-2"
          >
            {action.label}
            <ArrowRightIcon size={16} />
          </Button>
        </div>
      ))}
    </section>
  );
}
